import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import {
  faArrowRotateRight,
  faArrowsToDottedLine,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Col,
  Form,
  Row,
  type TableColumnsType,
  theme,
  Typography,
} from 'antd';
import {
  CreateEditLocationIntegrationModal,
  extractNonGroupItems,
} from 'pos-client/components/scheduleBoard/components/modals/CreateEditLocationIntegrationModal.tsx';
import { PosCard } from 'pos-client/components/shared/Display/Card/PosCard.tsx';
import { PosTable } from 'pos-client/components/shared/Display/Table/PosTable.tsx';
import { createFormFields } from 'pos-client/components/shared/Form/FormUtils.ts';
import { PosFormItem } from 'pos-client/components/shared/Form/PosFormItem.tsx';
import { PosInputSearch } from 'pos-client/components/shared/Form/Search/PosSearchInput.tsx';
import { useFilters } from 'pos-client/hooks/useFilters.tsx';
import { useSettingGroupsWithSettings } from 'pos-client/hooks/useSettings.tsx';
import { PosPageContent } from 'pos-client/layouts/page/PosPageContent.tsx';
import { convertISOStringToLocalDate } from 'pos-client/utils/dateHelpers.ts';
import { trpc } from 'pos-client/utils/trpc.ts';
import {
  LocationIntegrationFindFERequestSchema,
  type LocationIntegrationFindFERequestSchemaModel,
  type ServiceLocationConfigurationV2Model,
} from 'pos-shared/models/locationSchema.ts';

export const LocationIntegrationsTab = () => {
  const { t } = useTranslation();
  const { locationId } = useParams<'locationId'>();
  const { token } = theme.useToken();
  const [selectedIntegration, setSelectedIntegration] =
    useState<ServiceLocationConfigurationV2Model | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const form = useForm<LocationIntegrationFindFERequestSchemaModel>({
    mode: 'all',
    shouldFocusError: true,
    resolver: zodResolver(LocationIntegrationFindFERequestSchema),
    defaultValues: {
      name: '',
    },
  });

  const { formFilters, handleFilterChange, onPageChange, resetFilters } =
    useFilters<LocationIntegrationFindFERequestSchemaModel>({});

  /******************************************** REQUESTS **************************************************/

  const listOfNames = formFilters?.name ? [formFilters?.name] : [];

  const {
    data: locationIntegrations,
    isLoading: isLoadingLocationIntegrations,
  } = trpc.serviceLocation.getServiceLocationConfigV2.useQuery(
    {
      ...formFilters,
      listOfNames,
      listOfServiceLocationIds: [locationId as string],
    },
    {
      enabled: !!locationId,
    }
  );

  const { settings } = useSettingGroupsWithSettings({
    name: 'Integration',
    pageNumber: 1,
    resultsPerPage: 50,
  });

  const nonGroupItems = extractNonGroupItems(settings);

  /****************************************^ REQUESTS ^********************************************/

  const resetPaging = () => {
    onPageChange(1);
  };

  const resetFormAndFilters = () => {
    resetFilters();
  };

  const onSearchButtonClick = (value: string) => {
    handleFilterChange({
      filter: 'name',
      value: value ?? '',
    });

    resetPaging();
  };

  const handleAddIntegration = () => {
    setIsEditMode(false);
    setIsEditModalOpen(true);
  };

  const handleEditSelectedIntegration = (
    record: ServiceLocationConfigurationV2Model | null
  ) => {
    setIsEditMode(true);
    setIsEditModalOpen(record !== null);
    setSelectedIntegration(record);
  };

  const columns: TableColumnsType<ServiceLocationConfigurationV2Model> = [
    {
      key: 'name',
      title: t('locations.integrations.integrationNameLabel'),
      dataIndex: 'name',
      render: (_, record) => {
        const settingsItem = nonGroupItems.find(
          (item) => item.name === record.name
        );
        return <Typography.Text>{settingsItem?.name || ''}</Typography.Text>;
      },
    },
    {
      key: 'value',
      title: t('common.words.value'),
      dataIndex: 'value',
      render: (_, record) => {
        const settingsItem = nonGroupItems.find(
          (item) => item.name === record.name
        );

        const isPasswordType =
          settingsItem?.type && settingsItem?.type.toLowerCase() === 'password';
        return isPasswordType ? (
          '**********'
        ) : (
          <Typography.Text>{record?.value || ''}</Typography.Text>
        );
      },
    },
    {
      key: 'description',
      title: t('common.words.description'),
      dataIndex: 'description',
      render: (_, record) => {
        const settingsItem = nonGroupItems.find(
          (item) => item.name === record.name
        );
        return (
          <Typography.Text>{settingsItem?.description || ''}</Typography.Text>
        );
      },
    },
    {
      key: 'dateUpdated',
      title: t('common.audit.dateModified'),
      dataIndex: 'dateUpdated',
      width: '10%',
      render: (_, record) => {
        const settingsItem = nonGroupItems.find(
          (item) => item.name === record.name
        );
        return (
          <Typography.Text>
            {record.dateUpdated
              ? convertISOStringToLocalDate(
                  settingsItem?.modifiedDate?.toString()
                )
              : '-'}
          </Typography.Text>
        );
      },
    },
    {
      key: 'actions',
      width: '5%',
      render: (_, record) => {
        return (
          <Row wrap={false} justify={'center'}>
            <Button
              variant="solid"
              type="primary"
              onClick={() => handleEditSelectedIntegration(record)}
            >
              {t('common.buttons.edit')}
            </Button>
          </Row>
        );
      },
    },
  ];

  const filteredConfigurations = locationIntegrations?.results?.filter(
    (config, index) => {
      if (!config.key) config.key = index;
      return config.name !== 'staticFinalManifestRunTime';
    }
  );
  const hasFilters = !!formFilters?.name;
  const fields = createFormFields(form);

  return (
    <>
      <PosPageContent>
        <PosCard
          loading={isLoadingLocationIntegrations}
          headerProps={{
            title: t('locations.integrations.title'),
            icon: faArrowsToDottedLine,
            action: (
              <Button
                onClick={handleAddIntegration}
                icon={<FontAwesomeIcon icon={faArrowsToDottedLine} />}
              >
                {t('locations.integrations.addIntegration')}
              </Button>
            ),
          }}
          colProps={{
            span: 24,
          }}
        >
          <Form fields={fields}>
            <Row gutter={[token.marginSM, token.marginSM]}>
              <Col span={24} md={12} lg={8} xxl={6}>
                <PosFormItem
                  name="integrationName"
                  control={form.control}
                  noStyle
                >
                  <PosInputSearch
                    onSearch={(value: string) =>
                      onSearchButtonClick(value.trim())
                    }
                    placeholder={t(
                      'locations.integrations.integrationNameLabel'
                    )}
                    size="large"
                    disabled={isLoadingLocationIntegrations}
                    loading={isLoadingLocationIntegrations}
                  />
                </PosFormItem>
              </Col>

              {hasFilters && (
                <Col span={2}>
                  <Button
                    onClick={resetFormAndFilters}
                    icon={<FontAwesomeIcon icon={faArrowRotateRight} />}
                    size="large"
                  >
                    {t('common.buttons.resetAll')}
                  </Button>
                </Col>
              )}
            </Row>

            <PosTable
              id="location-integrations"
              dataSource={filteredConfigurations || []}
              columns={columns}
              isLoading={isLoadingLocationIntegrations}
            />
          </Form>
        </PosCard>
      </PosPageContent>

      {isEditModalOpen && (
        <CreateEditLocationIntegrationModal
          handleEditSelectedIntegration={handleEditSelectedIntegration}
          selectedIntegration={selectedIntegration}
          isEdit={isEditMode}
          isModalOpen={true}
          title={
            isEditMode
              ? t('locations.integrations.editLocationIntegration')
              : t('locations.integrations.addLocationIntegration')
          }
        />
      )}
    </>
  );
};
